import {mapValues, omitBy} from 'lodash';

import {createResourcesUpdatedReducer, defaultResourceMerger} from '../../../helpers/models/resourceHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {AppAction} from '../../appActions';
import {ResourceMap} from '../../appTypes';
import {CommentsActionsEnum} from '../comments/commentsActions';
import {ConversationsActionsEnum} from '../conversations/conversationsActions';
import {DataGroupsActionsEnum} from '../dataGroupsActions';
import {MessageActionsEnum} from '../messages/messagesActions';
import {ActivitiesActionsEnum} from './activitiesActions';

/*
 * Reducer.
 */

const activitiesUpdatedReducer = createResourcesUpdatedReducer(
  'activities',
  (activity) => activity.id,
  mergeActivity,
);

export function activitiesReducer(
  state: ResourceMap<ActivityModel>,
  action: AppAction,
): ResourceMap<ActivityModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED:
      return activitiesUpdatedReducer(state, action);

    // Single activity deleted.
    case ActivitiesActionsEnum.ACTIVITY_DELETED:
      return deleteActivities(
        state,
        (activity) => activity.id === action.payload.activity.id,
        action.payload.deletedAt,
        action.payload.hasCommentTombstone,
      );

    case ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED:
    case ConversationsActionsEnum.CONVERSATION_DELETED:
    case ConversationsActionsEnum.CLEAR_CONVERSATION_CONTENT: {
      const {conversationId} = action.payload;
      return omitBy(state, (activity) => activity && activity.conversationId === conversationId);
    }

    case MessageActionsEnum.MESSAGE_DELETED: {
      return deleteActivities(
        state,
        (a) => a.visualType === ActivityVisualTypesEnum.MESSAGE && a.messageUid === action.payload.messageUid,
        action.payload.deletedAt,
        false,
      );
    }

    case CommentsActionsEnum.COMMENT_DELETED:
      return deleteActivities(
        state,
        (a) => a.visualType === ActivityVisualTypesEnum.COMMENT && a.commentUid === action.payload.commentUid,
        action.payload.deletedAt,
        action.payload.hasCommentTombstone,
      );

    default:
      return state;
  }
}

type DeleteActivityPredicate = (activity: ActivityModel) => boolean;
function deleteActivities(
  state: ResourceMap<ActivityModel>,
  predicate: DeleteActivityPredicate,
  deletedAt: number,
  hasCommentTombstone: boolean,
): ResourceMap<ActivityModel> {
  return mapValues(state, (activity) => {
    if (!activity || !predicate(activity)) {
      return activity;
    }

    if (hasCommentTombstone && activity.visualType === ActivityVisualTypesEnum.COMMENT) {
      return {
        ...activity,
        isTombstoned: true,
      };
    }

    return {
      ...activity,
      deletedAt,
    };
  });
}

/*
 * Merger.
 */

function mergeActivity(existing: ActivityModel | undefined, updated: ActivityModel) {
  // If the activity is deleted, keep the deleted at.
  if (existing && existing.deletedAt) {
    return {
      ...updated,
      deletedAt: existing.deletedAt,
    };
  }

  return defaultResourceMerger(existing, updated);
}
